import {
  AIAuthorDetectionInputDetection,
  AIAuthorRunbookInputDetection,
  AIAuthorSkillInputSkill,
  AISchemaInferenceInput,
  AISummarizeKind,
  AISummarizeMetadata,
  LogStreamType,
  OutputLength,
} from 'Generated/schema';
import {
  ENTRY_POINT_ALERT,
  ENTRY_POINT_ALERT_LIST,
  ENTRY_POINT_DETECTION_AUTHOR,
  ENTRY_POINT_DETECTION_OVERVIEW,
  ENTRY_POINT_DETECTION_TESTS,
  ENTRY_POINT_DRILL_DOWN,
  ENTRY_POINT_EVENTS,
  ENTRY_POINT_ORGANIZATION_PROFILE,
  ENTRY_POINT_OVERVIEW,
  ENTRY_POINT_PANTHER_FLOW_QUERY,
  ENTRY_POINT_SEARCH,
  ENTRY_POINT_REPLAY,
  ENTRY_POINT_RUNBOOK_AUTHOR,
  ENTRY_POINT_SCHEMA_INFERENCE,
  ENTRY_POINT_SKILL_AUTHOR,
  ENTRY_POINT_SQL_QUERY_AUTHOR,
} from 'Generated/aiEntryPoints.generated';
import { PANTHER_AI_NAME } from 'Source/constants';
import {
  AiSummarizeDetection,
  useAiSummarizeDetection,
} from 'Components/AI/DetectionAIResponse/graphql/aiSummarizeDetection.generated';
import { useRunAISummarizeAlert } from 'Components/AIExplainLogEvents/graphql/runAiSummarizeAlert.generated';
import { useRunAISummarizeLogEvents } from 'Components/AIExplainLogEvents/graphql/runAiSummarizeLogEvents.generated';
import { useRunAISummarizeQueryResults } from 'Components/AIExplainLogEvents/graphql/runAiSummarizeQueryResults.generated';
import type { RunAISummarizeAlert } from 'Components/AIExplainLogEvents/graphql/runAiSummarizeAlert.generated';
import type { RunAISummarizeLogEvents } from 'Components/AIExplainLogEvents/graphql/runAiSummarizeLogEvents.generated';
import type { RunAISummarizeQueryResults } from 'Components/AIExplainLogEvents/graphql/runAiSummarizeQueryResults.generated';
import {
  RunAISchemaInference,
  useRunAISchemaInference,
} from '../graphql/runAiSchemaInference.generated';
import {
  RunAiAuthorDetection,
  useRunAiAuthorDetection,
} from '../graphql/runAiAuthorDetection.generated';
import {
  RunAiGeneratePantherFlowQuery,
  useRunAiGeneratePantherFlowQuery,
} from '../graphql/runAiGeneratePantherFlowQuery.generated';
import {
  RunAiGenerateSqlQuery,
  useRunAiGenerateSqlQuery,
} from '../graphql/runAiGenerateSqlQuery.generated';
import {
  RunAIReplayDetection,
  useRunAIReplayDetection,
} from '../graphql/runAiReplayDetection.generated';
import { RunAiAuthorSkill, useRunAiAuthorSkill } from '../graphql/runAiAuthorSkill.generated';
import { RunAiAuthorRunbook, useRunAiAuthorRunbook } from '../graphql/runAiAuthorRunbook.generated';
import {
  RunAiAuthorOrganizationProfile,
  useRunAiAuthorOrganizationProfile,
} from '../graphql/runAiAuthorOrganizationProfile.generated';
/**
 * Union of all AI mutation response types.
 * Used to type onCompleted callbacks that handle responses from any AI mutation kind.
 */
export type AIMutationResponseData =
  | RunAISummarizeAlert
  | RunAISummarizeLogEvents
  | RunAISummarizeQueryResults
  | RunAISchemaInference
  | AiSummarizeDetection
  | RunAiAuthorDetection
  | RunAiAuthorSkill
  | RunAiAuthorRunbook
  | RunAIReplayDetection
  | RunAiAuthorOrganizationProfile
  | RunAiGeneratePantherFlowQuery
  | RunAiGenerateSqlQuery;

/**
 * Type for AI mutation hooks used in dynamic dispatch.
 * These hooks share a common Apollo MutationTuple return shape but differ in
 * TData and TVariables generics. The dynamic dispatch pattern (getMutationHookForKind)
 * prevents full static narrowing, so we type the common callable shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AIMutationHook = (...args: any[]) => any;

/**
 * Maps AI summarize kinds to their corresponding page names for metadata purposes
 */
export const AI_PAGE_MAP: Record<AISummarizeKind, string> = {
  [AISummarizeKind.Alert]: ENTRY_POINT_ALERT,
  [AISummarizeKind.QueryResults]: ENTRY_POINT_SEARCH,
  [AISummarizeKind.Events]: ENTRY_POINT_EVENTS,
  [AISummarizeKind.DrillDown]: ENTRY_POINT_DRILL_DOWN,
  [AISummarizeKind.AlertList]: ENTRY_POINT_ALERT_LIST,
  [AISummarizeKind.DetectionOverview]: ENTRY_POINT_DETECTION_OVERVIEW,
  [AISummarizeKind.DetectionTests]: ENTRY_POINT_DETECTION_TESTS,
  [AISummarizeKind.Overview]: ENTRY_POINT_OVERVIEW,
  [AISummarizeKind.SchemaInference]: ENTRY_POINT_SCHEMA_INFERENCE,
  [AISummarizeKind.DetectionAuthor]: ENTRY_POINT_DETECTION_AUTHOR,
  [AISummarizeKind.PantherFlowQuery]: ENTRY_POINT_PANTHER_FLOW_QUERY,
  [AISummarizeKind.Replay]: ENTRY_POINT_REPLAY,
  [AISummarizeKind.RunbookAuthor]: ENTRY_POINT_RUNBOOK_AUTHOR,
  [AISummarizeKind.SkillAuthor]: ENTRY_POINT_SKILL_AUTHOR,
  [AISummarizeKind.SqlQueryAuthor]: ENTRY_POINT_SQL_QUERY_AUTHOR,
  [AISummarizeKind.OrganizationProfile]: ENTRY_POINT_ORGANIZATION_PROFILE,
} as const;

/**
 * Gets the page name for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding page name or 'unknown' if not found
 */
export const getPageNameForKind = (kind: AISummarizeKind): string => {
  return AI_PAGE_MAP[kind] || 'unknown';
};

/**
 * Maps AI summarize kinds to their corresponding labels for UI display
 */
export const AI_LABEL_MAP: Record<
  AISummarizeKind,
  {
    title: string;
    historyTitle: string;
    actionLabel: string;
    startButtonText: string;
    viewButtonText: string;
  }
> = {
  [AISummarizeKind.Alert]: {
    title: 'Triage',
    historyTitle: 'AI triage history',
    actionLabel: 'triage',
    startButtonText: `Start ${PANTHER_AI_NAME} Triage`,
    viewButtonText: `View ${PANTHER_AI_NAME} Triage`,
  },
  [AISummarizeKind.QueryResults]: {
    title: 'Summary',
    historyTitle: 'AI summary history',
    actionLabel: 'summary',
    startButtonText: 'Summarize with AI',
    viewButtonText: 'View AI Summary',
  },
  [AISummarizeKind.Events]: {
    title: 'Summary',
    historyTitle: 'AI summary history',
    actionLabel: 'summary',
    startButtonText: 'Start AI Analysis',
    viewButtonText: 'View AI Summary',
  },
  [AISummarizeKind.DrillDown]: {
    title: 'Summary',
    historyTitle: 'AI summary history',
    actionLabel: 'summary',
    startButtonText: 'Start AI Analysis',
    viewButtonText: 'View AI Summary',
  },
  [AISummarizeKind.AlertList]: {
    title: 'Triage',
    historyTitle: 'AI triage history',
    actionLabel: 'triage',
    startButtonText: 'Triage with AI',
    viewButtonText: 'View Multi-Alert Triage',
  },
  [AISummarizeKind.DetectionOverview]: {
    title: 'Overview',
    historyTitle: 'AI overview history',
    actionLabel: 'overview',
    startButtonText: 'Generate Overview',
    viewButtonText: 'View Overview',
  },
  [AISummarizeKind.DetectionTests]: {
    title: 'Analysis',
    historyTitle: 'AI analysis history',
    actionLabel: 'analysis',
    startButtonText: 'Generate Tests',
    viewButtonText: 'View Generated Tests',
  },
  [AISummarizeKind.Overview]: {
    title: 'Summary',
    historyTitle: 'AI summary history',
    actionLabel: 'summary',
    startButtonText: 'Start AI Analysis',
    viewButtonText: 'View AI Summary',
  },
  [AISummarizeKind.SchemaInference]: {
    title: 'Schema Inference',
    historyTitle: 'AI schema inference history',
    actionLabel: 'schema inference',
    startButtonText: 'Start Schema Inference',
    viewButtonText: 'View Schema Inference',
  },
  [AISummarizeKind.DetectionAuthor]: {
    title: 'Detection Author',
    historyTitle: 'AI detection author history',
    actionLabel: 'detection author',
    startButtonText: 'Start Detection Author',
    viewButtonText: 'View Detection Author',
  },
  [AISummarizeKind.PantherFlowQuery]: {
    title: 'PantherFlow Query',
    historyTitle: 'AI pantherflow query history',
    actionLabel: 'pantherflow query',
    startButtonText: 'Start PantherFlow Query',
    viewButtonText: 'View PantherFlow Query',
  },
  [AISummarizeKind.Replay]: {
    title: 'Replay',
    historyTitle: 'AI replay history',
    actionLabel: 'replay',
    startButtonText: 'Start AI Replay',
    viewButtonText: 'View AI Replay',
  },
  [AISummarizeKind.RunbookAuthor]: {
    title: 'Runbook Co-Author',
    historyTitle: 'AI runbook co-author history',
    actionLabel: 'runbook co-author',
    startButtonText: 'Draft Runbook with AI',
    viewButtonText: 'View Runbook Draft',
  },
  [AISummarizeKind.SkillAuthor]: {
    title: 'Skill Author',
    historyTitle: 'AI skill author history',
    actionLabel: 'skill author',
    startButtonText: 'Start Skill Author',
    viewButtonText: 'View Skill Author',
  },
  [AISummarizeKind.SqlQueryAuthor]: {
    title: 'SQL Query',
    historyTitle: 'AI SQL query history',
    actionLabel: 'sql query',
    startButtonText: 'Generate SQL Query',
    viewButtonText: 'View SQL Query',
  },
  [AISummarizeKind.OrganizationProfile]: {
    title: 'Profile Builder',
    historyTitle: 'AI profile builder history',
    actionLabel: 'profile builder',
    startButtonText: 'Start AI Profile Builder',
    viewButtonText: 'View AI Profile Builder',
  },
} as const;

/**
 * Gets the title label for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding title label
 */
export const getTitleForKind = (kind: AISummarizeKind): string => {
  return AI_LABEL_MAP[kind]?.title || 'Summary';
};

/**
 * Gets the history title for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding history title
 */
export const getHistoryTitleForKind = (kind: AISummarizeKind): string => {
  return AI_LABEL_MAP[kind]?.historyTitle || 'AI summary history';
};

/**
 * Gets the action label for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding action label
 */
export const getActionLabelForKind = (kind: AISummarizeKind): string => {
  return AI_LABEL_MAP[kind]?.actionLabel || 'summary';
};

/**
 * Gets the start button text for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding start button text
 */
export const getStartButtonTextForKind = (kind: AISummarizeKind): string => {
  return AI_LABEL_MAP[kind]?.startButtonText || 'Start AI Analysis';
};

/**
 * Gets the view button text for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding view button text
 */
export const getViewButtonTextForKind = (kind: AISummarizeKind): string => {
  return AI_LABEL_MAP[kind]?.viewButtonText || 'View AI Summary';
};

/**
 * Maps AI summarize kinds to their corresponding GraphQL mutation hooks
 */
export const AI_MUTATION_HOOKS: Record<AISummarizeKind, AIMutationHook> = {
  [AISummarizeKind.Alert]: useRunAISummarizeAlert,
  [AISummarizeKind.QueryResults]: useRunAISummarizeQueryResults,
  [AISummarizeKind.Events]: useRunAISummarizeLogEvents,
  [AISummarizeKind.DrillDown]: useRunAISummarizeLogEvents,
  [AISummarizeKind.AlertList]: useRunAISummarizeLogEvents,
  [AISummarizeKind.DetectionOverview]: useAiSummarizeDetection,
  [AISummarizeKind.DetectionTests]: useRunAISummarizeLogEvents,
  [AISummarizeKind.Overview]: useRunAISummarizeLogEvents,
  [AISummarizeKind.SchemaInference]: useRunAISchemaInference,
  [AISummarizeKind.DetectionAuthor]: useRunAiAuthorDetection,
  [AISummarizeKind.PantherFlowQuery]: useRunAiGeneratePantherFlowQuery,
  [AISummarizeKind.Replay]: useRunAIReplayDetection,
  [AISummarizeKind.RunbookAuthor]: useRunAiAuthorRunbook,
  [AISummarizeKind.SkillAuthor]: useRunAiAuthorSkill,
  [AISummarizeKind.SqlQueryAuthor]: useRunAiGenerateSqlQuery,
  [AISummarizeKind.OrganizationProfile]: useRunAiAuthorOrganizationProfile,
} as const;

/**
 * Gets the appropriate GraphQL mutation hook for a given AI summarize kind
 * @param kind The AI summarize kind
 * @returns The corresponding mutation hook
 */
export const getMutationHookForKind = (kind: AISummarizeKind) => {
  return AI_MUTATION_HOOKS[kind] || useRunAISummarizeLogEvents;
};

/**
 * Extracts the AI inference stream from a GraphQL response based on the AI summarize kind
 * @param kind The AI summarize kind
 * @param data The GraphQL response data
 * @returns The extracted AI inference stream
 */
export const extractAIInferenceStreamFromResponse = (
  kind: AISummarizeKind,
  data: AIMutationResponseData,
): RunAISummarizeAlert['aiSummarizeAlert'] => {
  if (kind === AISummarizeKind.Alert) {
    return (data as RunAISummarizeAlert).aiSummarizeAlert;
  }
  if (kind === AISummarizeKind.QueryResults) {
    return (data as RunAISummarizeQueryResults).aiSummarizeQueryResults;
  }
  if (kind === AISummarizeKind.DetectionOverview) {
    return (data as AiSummarizeDetection).aiSummarizeDetection;
  }
  if (kind === AISummarizeKind.SchemaInference) {
    return (data as RunAISchemaInference).aiSchemaInference;
  }
  if (kind === AISummarizeKind.DetectionAuthor) {
    return (data as RunAiAuthorDetection).aiAuthorDetection;
  }
  if (kind === AISummarizeKind.SkillAuthor) {
    return (data as RunAiAuthorSkill).aiAuthorSkill;
  }
  if (kind === AISummarizeKind.RunbookAuthor) {
    return (data as RunAiAuthorRunbook).aiAuthorRunbook;
  }
  if (kind === AISummarizeKind.PantherFlowQuery) {
    return (data as RunAiGeneratePantherFlowQuery).aiGeneratePantherFlowQuery;
  }
  if (kind === AISummarizeKind.SqlQueryAuthor) {
    return (data as RunAiGenerateSqlQuery).aiGenerateSqlQuery;
  }
  if (kind === AISummarizeKind.Replay) {
    return (data as RunAIReplayDetection).aiReplayDetection;
  }
  if (kind === AISummarizeKind.OrganizationProfile) {
    return (data as RunAiAuthorOrganizationProfile).aiAuthorOrganizationProfile;
  }
  return (data as RunAISummarizeLogEvents).aiSummarizeLogEvents;
};

/**
 * AI kinds whose GraphQL input type includes the `attachments` field.
 * Used to conditionally show/hide the attachment UI.
 *
 * NOTE: The GraphQL schema also supports `attachments` on AISummarizeLogEventsInput
 * (used by Events, AlertList, DetectionTests), but the UI intentionally limits
 * attachment support to these kinds for the initial rollout.
 */
export const KINDS_SUPPORTING_ATTACHMENTS = new Set<AISummarizeKind>([
  AISummarizeKind.Alert,
  AISummarizeKind.QueryResults,
  AISummarizeKind.Overview,
  AISummarizeKind.DrillDown,
]);

/**
 * Builds the appropriate input object for AI summarize mutations based on kind
 * @param kind The AI summarize kind
 * @param alias The alias (alertId, queryId, or general alias)
 * @param baseInput Base input properties common to all mutations
 * @param events Optional events array for log events mutations
 * @returns The constructed input object
 */
interface AISummarizeBaseInput {
  prompt?: string;
  outputLength?: OutputLength;
  metadata?: AISummarizeMetadata;
  private?: boolean;
  attachments?: string[];
}

export const buildAISummarizeInput = (
  kind: AISummarizeKind,
  alias: string,
  baseInput: AISummarizeBaseInput,
  events?: string[],
  streamType?: LogStreamType,
  detection?: AIAuthorDetectionInputDetection,
  skill?: AIAuthorSkillInputSkill,
  currentProfile?: string,
  runbookDetection?: AIAuthorRunbookInputDetection,
) => {
  const { attachments, ...rest } = baseInput;

  if (kind === AISummarizeKind.Alert) {
    return { ...rest, alertId: alias, attachments };
  }
  if (kind === AISummarizeKind.QueryResults) {
    return { ...rest, queryId: alias, attachments };
  }
  if (kind === AISummarizeKind.DetectionOverview) {
    return { ...rest, detectionId: alias };
  }
  if (kind === AISummarizeKind.SchemaInference) {
    const payload: AISchemaInferenceInput = {
      ...rest,
      sessionId: alias,
      streamType: streamType || LogStreamType.Auto,
    };
    return payload;
  }
  if (kind === AISummarizeKind.DetectionAuthor) {
    return {
      ...rest,
      detection,
    };
  }
  if (kind === AISummarizeKind.SkillAuthor) {
    return {
      ...rest,
      skill,
    };
  }
  if (kind === AISummarizeKind.RunbookAuthor) {
    return {
      ...rest,
      detection: runbookDetection,
    };
  }
  if (kind === AISummarizeKind.OrganizationProfile) {
    return {
      ...rest,
      currentProfile,
    };
  }
  // For log events mutations (including Overview, DrillDown), default to empty array if no events provided
  return { ...rest, alias, events: events || [], attachments };
};
